import React, { useState } from 'react';

const ShapeIcon = ({ shape, color, size = 16 }) => {
  const s = size;
  const half = s / 2;
  switch (shape) {
    case 'ellipse':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <circle cx={half} cy={half} r={half - 1} fill={color} stroke="#000" strokeWidth="1" opacity="0.9" />
        </svg>
      );
    case 'rectangle':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="3" width={s - 2} height={s - 6} rx="2" fill={color} stroke="#000" strokeWidth="1" opacity="0.9" />
        </svg>
      );
    case 'predefinedProcess':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="3" width={s - 2} height={s - 6} rx="1" fill={color} stroke="#000" strokeWidth="1" opacity="0.9" />
          <line x1="3" y1="3" x2="3" y2={s - 3} stroke="#000" strokeWidth="0.8" />
          <line x1={s - 3} y1="3" x2={s - 3} y2={s - 3} stroke="#000" strokeWidth="0.8" />
        </svg>
      );
    case 'rhombus':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <polygon points={`${half},1 ${s - 1},${half} ${half},${s - 1} 1,${half}`} fill={color} stroke="#000" strokeWidth="1" opacity="0.9" />
        </svg>
      );
    case 'triangle':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <polygon points={`${half},1 ${s - 1},${s - 1} 1,${s - 1}`} fill={color} stroke="#000" strokeWidth="1" opacity="0.9" />
        </svg>
      );
    case 'hexagon':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <polygon points={`${s * 0.25},1 ${s * 0.75},1 ${s - 1},${half} ${s * 0.75},${s - 1} ${s * 0.25},${s - 1} 1,${half}`} fill={color} stroke="#000" strokeWidth="1" opacity="0.9" />
        </svg>
      );
    case 'cylinder':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <ellipse cx={half} cy="4" rx={half - 1} ry="3" fill={color} stroke="#000" strokeWidth="0.8" opacity="0.9" />
          <rect x="1" y="4" width={s - 2} height={s - 8} fill={color} stroke="none" opacity="0.9" />
          <line x1="1" y1="4" x2="1" y2={s - 4} stroke="#000" strokeWidth="0.8" />
          <line x1={s - 1} y1="4" x2={s - 1} y2={s - 4} stroke="#000" strokeWidth="0.8" />
          <ellipse cx={half} cy={s - 4} rx={half - 1} ry="3" fill={color} stroke="#000" strokeWidth="0.8" opacity="0.85" />
        </svg>
      );
    default:
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} rx="3" fill={color} stroke="#000" strokeWidth="1" opacity="0.9" />
        </svg>
      );
  }
};

const BTN_SIZE = 28;

const FlowChartToolbar = ({
  workflowData,
  isDesignMode,
  setIsDesignMode,
  showCodeEditor,
  setShowCodeEditor,
  selectedShape,
  onBulkUpdateScriptsFromComments,
  isBulkUpdatingScriptsFromComments,
  onCompileScriptsFromJsonPrompts,
  isCompilingScriptsFromJsonPrompts,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onSave,
  isSaving,
  onFetchFxGroups,
  isLoadingFxGroups,
  onMoveRequest,
  onClose,
  showTerminal,
  onToggleTerminal,
  shapes,
  addShape,
}) => {
  const [showInfo, setShowInfo] = useState(false);

  const btn = (active = false, color = null) => ({
    width: BTN_SIZE,
    height: BTN_SIZE,
    minWidth: BTN_SIZE,
    minHeight: BTN_SIZE,
    background: active ? (color ? `${color}22` : '#3e3e42') : '#2a2a2e',
    color: active ? (color || '#e6edf3') : '#9d9d9d',
    border: `1px solid ${active ? (color ? `${color}55` : '#555') : '#3e3e42'}`,
    padding: 0,
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.12s',
    flexShrink: 0,
    lineHeight: 1,
    fontSize: 10,
    fontWeight: 700,
  });

  const hoverIn = (e, color) => {
    e.currentTarget.style.background = color ? `${color}22` : '#3e3e42';
    e.currentTarget.style.color = color || '#e6edf3';
    e.currentTarget.style.borderColor = color ? `${color}55` : '#555';
  };
  const hoverOut = (e, active, color) => {
    e.currentTarget.style.background = active ? (color ? `${color}22` : '#3e3e42') : '#2a2a2e';
    e.currentTarget.style.color = active ? (color || '#e6edf3') : '#9d9d9d';
    e.currentTarget.style.borderColor = active ? (color ? `${color}55` : '#555') : '#3e3e42';
  };

  const divider = (
    <div style={{ width: 1, height: 18, background: '#3e3e42', flexShrink: 0, margin: '0 2px' }} />
  );

  return (
    <div style={{
      background: '#1e1e1e',
      borderBottom: '1px solid #3e3e42',
      padding: '4px 8px',
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      minHeight: 36,
      position: 'relative',
    }}>
      {/* ── DiagRun Logo ── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexShrink: 0,
          padding: '0 6px 0 2px',
          height: BTN_SIZE,
          userSelect: 'none',
          cursor: 'default',
        }}
        title="DiagRun"
      >
        <span
          style={{
            fontFamily: "'Inter', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 14,
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '0.5px',
            lineHeight: 1,
          }}
        >
          DiagRun
        </span>
        <div
          style={{
            width: '100%',
            height: 2.5,
            marginTop: 2,
            borderRadius: 2,
            background: 'linear-gradient(90deg, #b91c1c 0%, #dc2626 60%, #ef4444 100%)',
          }}
        />
      </div>

      {divider}

      {/* ── Kaydet ── */}
      <button
        title={isSaving ? 'Kaydediliyor…' : 'Kaydet (Ctrl+S)'}
        onClick={onSave}
        disabled={isSaving}
        style={{
          ...btn(false, '#238636'),
          opacity: isSaving ? 0.5 : 1,
          cursor: isSaving ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={(e) => { if (!isSaving) hoverIn(e, '#238636'); }}
        onMouseLeave={(e) => { if (!isSaving) hoverOut(e, false, '#238636'); }}
      >
        {isSaving ? (
          <div style={{ width: 12, height: 12, border: '2px solid #3e3e42', borderTopColor: '#4ade80', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
        )}
      </button>

      {divider}

      {/* ── Design Mode ── */}
      <button
        title={isDesignMode ? 'Design Mode (Aktif)' : 'Design Mode'}
        onClick={() => setIsDesignMode(!isDesignMode)}
        style={btn(isDesignMode, '#388bfd')}
        onMouseEnter={(e) => hoverIn(e, '#388bfd')}
        onMouseLeave={(e) => hoverOut(e, isDesignMode, '#388bfd')}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="12" y1="3" x2="12" y2="21" />
        </svg>
      </button>

      {/* ── Terminal ── */}
      <button
        title={showTerminal ? 'Terminal Kapat' : 'Terminal Aç'}
        onClick={onToggleTerminal}
        style={btn(showTerminal, '#4ade80')}
        onMouseEnter={(e) => hoverIn(e, '#4ade80')}
        onMouseLeave={(e) => hoverOut(e, showTerminal, '#4ade80')}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="4 17 10 11 4 5" />
          <line x1="12" y1="19" x2="20" y2="19" />
        </svg>
      </button>

      {divider}

      {/* ── Zoom In ── */}
      <button title="Zoom In (Ctrl+Mouse)" onClick={onZoomIn} style={btn()} onMouseEnter={(e) => hoverIn(e)} onMouseLeave={(e) => hoverOut(e, false)}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      </button>

      {/* ── 1:1 ── */}
      <button title="Zoom Sıfırla (1:1)" onClick={onZoomReset} style={btn()} onMouseEnter={(e) => hoverIn(e)} onMouseLeave={(e) => hoverOut(e, false)}>
        1:1
      </button>

      {/* ── Zoom Out ── */}
      <button title="Zoom Out" onClick={onZoomOut} style={btn()} onMouseEnter={(e) => hoverIn(e)} onMouseLeave={(e) => hoverOut(e, false)}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      </button>

      {divider}

      {/* ── Fx ── */}
      <button
        title="Fx Grupları"
        onClick={onFetchFxGroups}
        disabled={isLoadingFxGroups}
        style={{ ...btn(false, '#a78bfa'), opacity: isLoadingFxGroups ? 0.5 : 1, cursor: isLoadingFxGroups ? 'not-allowed' : 'pointer' }}
        onMouseEnter={(e) => { if (!isLoadingFxGroups) hoverIn(e, '#a78bfa'); }}
        onMouseLeave={(e) => { if (!isLoadingFxGroups) hoverOut(e, false, '#a78bfa'); }}
      >
        {isLoadingFxGroups ? (
          <div style={{ width: 12, height: 12, border: '2px solid #3e3e42', borderTopColor: '#a78bfa', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="18" y1="6" x2="18" y2="18" />
          </svg>
        )}
      </button>

      {/* ── Move Request ── */}
      <button
        title="Move Request — Talebi başka bir flow'a taşı"
        onClick={onMoveRequest}
        style={btn(false, '#fbbf24')}
        onMouseEnter={(e) => hoverIn(e, '#fbbf24')}
        onMouseLeave={(e) => hoverOut(e, false, '#fbbf24')}
      >
        <svg width="14" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="1" y="3" width="15" height="13" rx="1" />
          <path d="M16 8h4l3 5v4h-7V8z" />
          <circle cx="5.5" cy="18.5" r="2.5" />
          <circle cx="18.5" cy="18.5" r="2.5" />
        </svg>
      </button>

      {/* ── AI Compile ── */}
      <button
        title={isCompilingScriptsFromJsonPrompts ? 'Derleniyor…' : "AI: Prompt'a Göre Derle"}
        onClick={onCompileScriptsFromJsonPrompts}
        disabled={isCompilingScriptsFromJsonPrompts}
        style={{ ...btn(false, '#06b6d4'), opacity: isCompilingScriptsFromJsonPrompts ? 0.5 : 1, cursor: isCompilingScriptsFromJsonPrompts ? 'not-allowed' : 'pointer' }}
        onMouseEnter={(e) => { if (!isCompilingScriptsFromJsonPrompts) hoverIn(e, '#06b6d4'); }}
        onMouseLeave={(e) => { if (!isCompilingScriptsFromJsonPrompts) hoverOut(e, false, '#06b6d4'); }}
      >
        {isCompilingScriptsFromJsonPrompts ? (
          <div style={{ width: 12, height: 12, border: '2px solid #3e3e42', borderTopColor: '#06b6d4', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4l16 8-16 8V4z" strokeLinejoin="round" />
            <path d="M15 4l4 2" strokeLinecap="round" />
          </svg>
        )}
      </button>

      {divider}

      {/* ── Info ── */}
      <button
        title="Workflow Bilgileri"
        onClick={() => setShowInfo(!showInfo)}
        style={btn(showInfo, '#38bdf8')}
        onMouseEnter={(e) => hoverIn(e, '#38bdf8')}
        onMouseLeave={(e) => hoverOut(e, showInfo, '#38bdf8')}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </button>

      {/* ── Info Popup ── */}
      {showInfo && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 99999,
          background: '#161b22', border: '1px solid #30363d', borderRadius: 8,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          minWidth: 340, maxWidth: 500, maxHeight: 400,
          overflow: 'auto', padding: 16,
          fontFamily: "'Fira Mono', 'Consolas', monospace",
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ color: '#38bdf8', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em' }}>WORKFLOW BİLGİLERİ</span>
            <button onClick={() => setShowInfo(false)} style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
          {workflowData?.workflow && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: '#8b949e', fontSize: 10, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Workflow</div>
              {Object.entries(workflowData.workflow).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 3 }}>
                  <span style={{ color: '#79c0ff', fontSize: 11, minWidth: 80, flexShrink: 0 }}>{k}</span>
                  <span style={{ color: '#e6edf3', fontSize: 11, wordBreak: 'break-all' }}>{String(v ?? '')}</span>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(workflowData?.contents) && (
            <div>
              <div style={{ color: '#8b949e', fontSize: 10, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Steps ({workflowData.contents.length})
              </div>
              {workflowData.contents.filter(Boolean).map((c, i) => (
                <div key={i} style={{
                  background: '#0d1117', border: '1px solid #21262d', borderRadius: 4,
                  padding: '5px 9px', marginBottom: 4, display: 'flex', gap: 8, alignItems: 'center',
                }}>
                  <span style={{ color: '#484f58', fontSize: 10, minWidth: 20, flexShrink: 0 }}>#{c.id ?? i}</span>
                  <span style={{ color: '#ffa657', fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.label || c.filename || c.name || '—'}
                  </span>
                  {c.type && (
                    <span style={{ color: '#8b949e', fontSize: 10, background: '#21262d', padding: '1px 5px', borderRadius: 3 }}>{c.type}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {!workflowData && (
            <div style={{ color: '#484f58', fontSize: 12, textAlign: 'center', padding: 16 }}>Veri yok</div>
          )}
        </div>
      )}

      {/* ── Shapes ── */}
      {isDesignMode && Array.isArray(shapes) && shapes.length > 0 && (
        <>
          {divider}
          {shapes.map((s) => (
            <button
              key={s.name}
              title={s.label}
              onClick={() => addShape?.(s.name, s.label)}
              style={{
                width: BTN_SIZE,
                height: BTN_SIZE,
                minWidth: BTN_SIZE,
                minHeight: BTN_SIZE,
                background: 'transparent',
                border: '1px solid transparent',
                padding: 0,
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.12s',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${s.color}22`;
                e.currentTarget.style.borderColor = `${s.color}55`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              <ShapeIcon shape={s.mxShape || s.name} color={s.color} />
            </button>
          ))}
        </>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* ── Kapat ── */}
      <button
        title="Kapat"
        onClick={onClose}
        style={{ ...btn(false), color: '#6e7681' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#f85149'; e.currentTarget.style.background = '#f8514920'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#6e7681'; e.currentTarget.style.background = '#2a2a2e'; }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default FlowChartToolbar;
